import { FunctionComponent } from 'react';
import {
    ContingentCondition,
    ALL_PRODUCT_ENVIRONMENTS,
    ProductConfiguration,
} from '@/models/product';
import { v4 as uuid } from 'uuid';
import { useAuthentication } from '@/hooks/useAuthentication';
import { useNotification } from '@starjack/features/notifications';
import { ContingentConditionForm } from '@/pageContents/productConfiguration/ProductConfigurationDetail/conditions/ContingentConditionForm';
import { usePostOrPutConfigurationMutation } from '@/api/v2/productConfigurations';

const buildInitialContingentCondition = (
    pC: ProductConfiguration
): ContingentCondition => ({
    id: uuid(),
    productConfigurationId: pC.id,
    name: '',
    externalName: {
        de: '',
        en: '',
    },

    sort: undefined,
    minDaysBeforeTicketStart: null,

    validFrom: null,
    validTo: null,
    startDateValidFrom: null,
    startDateValidTo: null,
    validWeekdays: new Set<string>(),
    startDateValidWeekdays: new Set<string>(),
    personTypeIds: [],
    sellingGroup: null,
    notSellingGroup: null,
    maxTicketsPerStartDay: 0,
    environments: ALL_PRODUCT_ENVIRONMENTS,
});

type ContingentConditionDialogContainerProps = {
    onClose: () => void;
    productConfiguration: ProductConfiguration;
    condition?: ContingentCondition;
};

export const ContingentConditionFormContainer: FunctionComponent<
    React.PropsWithChildren<ContingentConditionDialogContainerProps>
> = ({ condition, productConfiguration, onClose }) => {
    const {
        liftOperatorData: { liftOperatorId },
    } = useAuthentication();
    const { addNotification } = useNotification();
    const [apiAction, { isLoading }] = usePostOrPutConfigurationMutation({
        liftOperatorId,
        productConfiguration,
    });

    const isNewCondition = !condition?.id;

    // save or update logic
    const saveOrUpdate = (nextCondition: ContingentCondition) => {
        const nextConfiguration = {
            ...productConfiguration,
        };
        if (!isNewCondition) {
            const conditionIndex =
                productConfiguration.contingentConditions.findIndex(
                    (c) => condition.id === c.id
                );

            if (conditionIndex > -1) {
                const contingentConditions = [
                    ...productConfiguration.contingentConditions,
                ];

                contingentConditions.splice(conditionIndex, 1, nextCondition);

                nextConfiguration.contingentConditions = contingentConditions;
            }
        } else {
            const highestSort = Math.max(
                ...nextConfiguration.contingentConditions.map((p) => p.sort)
            );
            nextConfiguration.contingentConditions = [
                ...nextConfiguration.contingentConditions,
                {
                    ...nextCondition,
                    sort: highestSort + 1,
                },
            ];
        }

        apiAction({ liftOperatorId, productConfiguration: nextConfiguration })
            .unwrap()
            .then(() => {
                addNotification({
                    message: 'Regel gespeichert',
                    type: 'success',
                });
                onClose();
            })
            .catch(() => {
                addNotification({
                    message: 'Fehler beim speichern',
                    type: 'error',
                });
            });
    };

    return (
        <ContingentConditionForm
            condition={
                condition ||
                buildInitialContingentCondition(productConfiguration)
            }
            isNewCondition={isNewCondition}
            onSave={saveOrUpdate}
            onClose={onClose}
            isLoading={isLoading}
        />
    );
};
